import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminDashboard from './admin/AdminDashboard';
import AdminUsers from './admin/AdminUsers';
import AdminCompanies from './admin/AdminCompanies';
import AdminUploads from './admin/AdminUploads';
import AdminInvoices from './admin/AdminInvoices';
import AdminAuditLogs from './admin/AdminAuditLogs';
import API_ENDPOINTS from '../config';
import Navbar from './Navbar';
import logger from '../utils/logger';
import { getThemeColors } from '../utils/theme';

export default function Admin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Force dark theme for admin panel
  const isDark = true;
  const colors = getThemeColors(isDark);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Force dark mode when admin panel mounts
  useEffect(() => {
    const previousTheme = document.documentElement.getAttribute('data-theme');
    const previousBodyClass = document.body.classList.contains('dark');
    const previousHtmlClass = document.documentElement.classList.contains('dark');
    
    // Apply dark theme
    document.documentElement.setAttribute('data-theme', 'dark');
    document.documentElement.classList.add('dark');
    document.body.classList.add('dark');
    
    // Restore previous theme when component unmounts
    return () => {
      if (previousTheme) {
        document.documentElement.setAttribute('data-theme', previousTheme);
      } else {
        document.documentElement.removeAttribute('data-theme');
      }
      
      if (previousBodyClass) {
        document.body.classList.add('dark');
      } else {
        document.body.classList.remove('dark');
      }
      
      if (previousHtmlClass) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };
  }, []); // Only run on mount/unmount

  const checkAdminAccess = useCallback(async () => {
    try {
      // Check admin access using cookie-based auth
      const profileResponse = await fetch(`${API_ENDPOINTS.PROFILE}`, {
        method: 'GET',
        credentials: 'include', // Include cookies
      });

      logger.log('Profile response status:', profileResponse.status);

      if (!profileResponse.ok) {
        if (profileResponse.status === 401) {
          logger.log('Not authenticated, redirecting to login');
          navigate('/login');
          return;
        }
        const errorText = await profileResponse.text();
        logger.error('Profile fetch failed - response text:', errorText);
        throw new Error(`Failed to fetch user profile: ${profileResponse.status}`);
      }

      // Check if response is JSON
      const contentType = profileResponse.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await profileResponse.text();
        logger.error('Profile response is not JSON:', responseText.substring(0, 200));
        throw new Error('Profile endpoint returned non-JSON response');
      }

      const userData = await profileResponse.json();
      logger.log('User data:', userData);
      
      // Check if userData has the correct structure
      const user = userData.user || userData;
      
      if (user.role !== 'admin') {
        logger.log('User is not admin, redirecting to dashboard');
        navigate('/dashboard');
        return;
      }

      logger.log('Admin access granted');
      setUser(user);
    } catch (error) {
      logger.error('Error checking admin access:', error);
      // Don't immediately redirect to login, show error first
      setError(t('admin.accessError', { error: error.message }));
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    checkAdminAccess();
  }, [checkAdminAccess]);

  const handleLogout = async () => {
    try {
      // Call logout endpoint to clear cookie
      await fetch(API_ENDPOINTS.LOGOUT, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      logger.error('Logout error:', err);
    } finally {
      // Navigate to login regardless of API call success
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: colors.backgroundLight
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>🔒</div>
          <div style={{ fontSize: '18px', color: colors.textSecondary }}>{t('admin.checkingAccess')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: colors.backgroundLight
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>❌</div>
          <div style={{ fontSize: '18px', color: colors.error, marginBottom: '16px' }}>{error}</div>
          <button
            onClick={() => {
              setError(null);
              setLoading(true);
              checkAdminAccess();
            }}
            style={{
              padding: '8px 16px',
              background: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = colors.primaryHover}
            onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
          >
            {t('admin.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: t('admin.title'), icon: '📊' },
    { id: 'users', label: t('admin.navUsers'), icon: '👥' },
    { id: 'companies', label: t('admin.navCompanies'), icon: '🏢' },
    { id: 'uploads', label: t('admin.files'), icon: '📁' },
    { id: 'invoices', label: t('admin.navInvoices'), icon: '📄' },
    { id: 'audit', label: t('admin.navAuditLogs'), icon: '📋' }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AdminDashboard />;
      case 'users':
        return <AdminUsers />;
      case 'companies':
        return <AdminCompanies />;
      case 'uploads':
        return <AdminUploads />;
      case 'invoices':
        return <AdminInvoices />;
      case 'audit':
        return <AdminAuditLogs />;
      default:
        return <AdminDashboard />;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: colors.backgroundLight }}>
      <Navbar scriptMode="latin" onScriptModeChange={() => {}} />
      
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
        {/* Sidebar */}
        <div style={{
          width: '280px',
          background: colors.card,
          borderRight: `1px solid ${colors.border}`,
          padding: '2rem 0',
          boxShadow: isDark ? '2px 0 4px rgba(0,0,0,0.3)' : '2px 0 4px rgba(0,0,0,0.1)'
        }}>
          {/* Admin Header */}
          <div style={{ padding: '0 2rem 2rem 2rem', borderBottom: `1px solid ${colors.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <span style={{ fontSize: '24px' }}>👑</span>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: colors.text }}>
                {t('admin.panel')}
              </h2>
            </div>
            <p style={{ margin: 0, fontSize: '14px', color: colors.textSecondary }}>
                {t('admin.welcome', { firstName: user?.firstName || '', lastName: user?.lastName || '' })}
            </p>
          </div>

          {/* Navigation Tabs */}
          <div style={{ padding: '1rem 0' }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  width: '100%',
                  padding: '12px 2rem',
                  background: activeTab === tab.id ? colors.primary : 'transparent',
                  color: activeTab === tab.id ? 'white' : colors.text,
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontSize: '16px',
                  fontWeight: activeTab === tab.id ? 600 : 500,
                  transition: 'all 0.2s',
                  borderLeft: activeTab === tab.id ? `4px solid ${colors.primaryHover}` : '4px solid transparent'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.background = colors.backgroundHover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: '18px' }}>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Quick Actions */}
          <div style={{ padding: '2rem', borderTop: `1px solid ${colors.border}` }}>
            <h4 style={{ margin: '0 0 1rem 0', fontSize: '14px', fontWeight: 600, color: colors.text }}>
              {t('admin.quickActions')}
            </h4>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: colors.backgroundHover,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                color: colors.text,
                marginBottom: '8px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = colors.border}
              onMouseLeave={(e) => e.target.style.backgroundColor = colors.backgroundHover}
            >
              {t('admin.backToDashboard')}
            </button>
            <button
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: isDark ? '#7f1d1d' : '#fee2e2',
                border: `1px solid ${isDark ? '#991b1b' : '#fecaca'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                color: colors.error,
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = isDark ? '#991b1b' : '#fecaca'}
              onMouseLeave={(e) => e.target.style.backgroundColor = isDark ? '#7f1d1d' : '#fee2e2'}
            >
              🚪 {t('auth.logout')}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, padding: '2rem' }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
} 